import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase/client';
import { getProfile, type Profile } from '@/lib/api/profiles';
import { AuthContext, type AuthStatus } from './context';

function deriveStatus(user: User | null, profile: Profile | null): AuthStatus {
  if (!user) return 'unauthenticated';
  if (!profile || profile.handle === null) return 'needs-handle';
  return 'authenticated';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');

  const loadProfile = useCallback(async (user: User | null) => {
    if (!user) {
      setProfile(null);
      setStatus('unauthenticated');
      return;
    }
    try {
      const p = await getProfile(user.id);
      setProfile(p);
      setStatus(deriveStatus(user, p));
    } catch {
      setProfile(null);
      setStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      loadProfile(data.session?.user ?? null);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      loadProfile(nextSession?.user ?? null);
    });

    return () => sub.subscription.unsubscribe();
  }, [loadProfile]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const value = useMemo(
    () => ({ user: session?.user ?? null, session, profile, status, signOut }),
    [session, profile, status, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
